/**
 * Builds the URL to open the Asana bug-intake form with tester context
 * pre-filled. Amanda gives us back a base URL with placeholder param names;
 * we substitute the actual tester values at runtime.
 *
 * The placeholder names in `asana_bug_form_url` are expected to match the
 * Asana custom-field URL keys. The exact key strings depend on what
 * Amanda gets back from Asana; for now we treat the URL as a template
 * with these substitution tokens:
 *   {EMAIL}, {WP_ID}, {DAY}, {CONDITION}
 *
 * If the form URL doesn't contain those tokens, we'll fall back to
 * appending them as query params. Either way, the tester's "No" tap
 * opens a new tab with as much pre-fill as possible.
 */

interface BugContext {
  email: string;
  wordpressUserId: string | null;
  dayLabel: string; // e.g. "Day 4 of 7"
  dayDate: string; // e.g. "Sat, Apr 18"
  conditionLabel: string; // e.g. "Profile - Active Minutes (Day 3 - Fri Apr 17)"
}

export function buildAsanaUrl(formUrl: string, ctx: BugContext): string {
  if (!formUrl) {
    return "about:blank";
  }
  const day = `${ctx.dayLabel} (${ctx.dayDate})`;
  const wp = ctx.wordpressUserId ?? "";

  // Token-based substitution if the URL has the placeholders.
  const hasTokens = /\{EMAIL\}|\{WP_ID\}|\{DAY\}|\{CONDITION\}/.test(formUrl);
  if (hasTokens) {
    return formUrl
      .replace(/\{EMAIL\}/g, encodeURIComponent(ctx.email))
      .replace(/\{WP_ID\}/g, encodeURIComponent(wp))
      .replace(/\{DAY\}/g, encodeURIComponent(day))
      .replace(/\{CONDITION\}/g, encodeURIComponent(ctx.conditionLabel));
  }

  // Fallback: append as query params with our convention. Amanda or
  // Retronyms can rename via the admin Config field if Asana uses
  // different keys.
  const url = new URL(formUrl);
  url.searchParams.set("tester_email", ctx.email);
  if (wp) url.searchParams.set("wordpress_user_id", wp);
  url.searchParams.set("testing_day", day);
  url.searchParams.set("condition_flagged", ctx.conditionLabel);
  return url.toString();
}

/**
 * Open the bug form in a new tab with the supplied context. Use this
 * directly from a click handler when status === 'issue_found'.
 */
export function openAsanaBugForm(formUrl: string, ctx: BugContext): void {
  const target = buildAsanaUrl(formUrl, ctx);
  window.open(target, "_blank", "noopener,noreferrer");
}
